import React, { useEffect, useState } from 'react'
import { isEqual } from 'lodash-es'
import PropTypes from 'prop-types'

// Functions
import {
  formatValueToPercentOrDelta,
} from './index.functions'

// Style
import {
  Container,
  ValueContainer,
  ValueLabel,
  CustomLabel,
  ComparisonLabel,
} from './index.style'

import { 
  formatNumberDisplayed,
} from "../utils/global.js"

const EvolutionWidget = ({
  // PROPS
  dataChart,
  customLabel,
  numberDecimal,
  decimalSeparator,
  showSpaceForThousand,
  percentOrDelta,
  evolutionThemeStyle,
  valueData,
  dependsOnField,

  // STYLE
  comparisonColor,
  comparisonSize,
  comparisonWeight,
  iconPositiveSelected,
  iconPositiveColor,
  iconPositiveFontSize,
  iconNegativeSelected,
  iconNegativeColor,
  iconNegativeFontSize,
  iconNeutralFontSize,
  iconNeutralColor,
  iconNeutralSelected,
  customLabelColor,
  customLabelSize,
  customLabelWeight,
  valuePositiveColor,
  valuePositiveSize,
  valuePositiveWeight,
  valueNeutralColor,
  valueNeutralSize,
  valueNeutralWeight,
  valueNegativeColor,
  valueNegativeSize,
  valueNegativeWeight,
}) => {
  /* State */
  const iconInitialState = { 
    up: { icon: "fas fa-arrow-up", color: "#000000", fontSize: "1rem" },
    neutral: { icon: "fas fa-arrow-right", color: "#000000", fontSize: "1rem" },
    down: { icon: "fas fa-arrow-down", color: "#000000", fontSize: "1rem"},
  }
  const valueInitialState = {
    up: { color: "#000000", fontSize: "1rem", fontWeight: "normal" },
    neutral: { color: "#000000", fontSize: "1rem", fontWeight: "normal" },
    down: { color: "#000000", fontSize: "1rem", fontWeight: "normal" },
  }

  const [customLabelToShow, setCustomLabelToShow] = useState("")
  const [iconProps, setIconProps] = useState({...iconInitialState})
  const [valueProps, setValueProps] = useState({...valueInitialState})
  const [evolutionSign, setEvolutionSign] = useState("neutral")
  const [valueToShow, setValueToShow] = useState(0)

  /* Lifecycle */

  // IconProps
  useEffect(() => {
    if(evolutionThemeStyle === "defaultStyle"){
      if(!isEqual(iconProps, iconInitialState)){
        setIconProps({...iconInitialState})
      }
    } else if(evolutionThemeStyle === "customStyle"){
      const newStyle = {
        up: { icon: iconPositiveSelected, color: iconPositiveColor, fontSize: iconPositiveFontSize},
        neutral: { icon: iconNeutralSelected, color: iconNeutralColor, fontSize: iconNeutralFontSize},
        down: { icon: iconNegativeSelected, color: iconNegativeColor, fontSize: iconNegativeFontSize},
      }
      if(!isEqual(iconProps, newStyle)){
        setIconProps({...newStyle})
      }
    }
  },[
    iconNeutralFontSize,
    iconNeutralColor,
    iconNeutralSelected,
    iconNegativeFontSize,
    iconNegativeColor,
    iconNegativeSelected,
    iconPositiveFontSize,
    iconPositiveSelected,
    iconPositiveColor,
    evolutionThemeStyle,
  ])

  // ValueProps
  useEffect(() => {
    if(evolutionThemeStyle === "defaultStyle"){
      if(!isEqual(valueProps, valueInitialState)){
        setValueProps({...valueInitialState})
      }
    } else if(evolutionThemeStyle === "customStyle"){
      const newStyle = {
        up: { color: valuePositiveColor, fontSize: valuePositiveSize, fontWeight: valuePositiveWeight },
        neutral: { color: valueNeutralColor, fontSize: valueNeutralSize, fontWeight: valueNeutralWeight },
        down: { color: valueNegativeColor, fontSize: valueNegativeSize, fontWeight: valueNegativeWeight },
      }
      if(!isEqual(valueProps, newStyle)){
        setValueProps({...newStyle})
      }
    }
  },[
    valuePositiveColor,
    valuePositiveSize,
    valuePositiveWeight,
    valueNeutralColor,
    valueNeutralSize,
    valueNeutralWeight,
    valueNegativeColor,
    valueNegativeSize,
    valueNegativeWeight,
    evolutionThemeStyle,
  ])

  useEffect(() => {
    let newValue = 0

    let displayedValue = 0
    let referencedValue = 0

    if(newValue === 0 && dataChart && dataChart.length){
      if(dataChart[0].hasOwnProperty(valueData)){
        displayedValue = dataChart[0][valueData]
      }
      if(dataChart[0].hasOwnProperty(dependsOnField)){
        referencedValue = dataChart[0][dependsOnField]
      } 
    }
    newValue = formatValueToPercentOrDelta(percentOrDelta, displayedValue, referencedValue)


    if(newValue === 0 && dataChart && dataChart.length && dataChart[0].hasOwnProperty(valueData))
      newValue = dataChart[0][valueData]

    if(newValue === 0){
      setEvolutionSign("neutral")
    } else if(newValue >= 0){
      setEvolutionSign("up")
    } else if(newValue <= 0){
      setEvolutionSign("down")
    }

    newValue = formatNumberDisplayed(newValue, numberDecimal, showSpaceForThousand, decimalSeparator)
    setValueToShow(newValue)
  },[
    dataChart,
    numberDecimal,
    percentOrDelta,
    showSpaceForThousand,
    decimalSeparator,
  ])

  useEffect(() => {
    if(customLabel !== customLabelToShow){
      setCustomLabelToShow(customLabel)
    }
  }, [
    customLabel,
  ])  

  /* Render */
  return (
    <Container
      iconColor={iconProps[evolutionSign]?.color}
      iconFontSize={iconProps[evolutionSign]?.fontSize}
    >
      <i id="evolutionIcon" className={iconProps[evolutionSign]?.icon}/>
      <ValueContainer>
        <ValueLabel
          fontColor={valueProps[evolutionSign]?.color}
          fontSize={valueProps[evolutionSign]?.fontSize}
          fontWeight={valueProps[evolutionSign]?.fontWeight}
        >
          {`${valueToShow}${percentOrDelta === "percent" ? "%" : ""}`}
        </ValueLabel>

        <CustomLabel
          fontColor={customLabelColor}
          fontSize={customLabelSize}
          fontWeight={customLabelWeight}
        >
          {customLabelToShow}
        </CustomLabel>
      </ValueContainer>

      {/* Comparison period not available for now
       {<ComparisonLabel
        fontColor={comparisonColor}
        fontSize={comparisonSize}
        fontWeight={comparisonWeight}
      > 
        No comparison yet
      </ComparisonLabel>} */}
    </Container>
  )
}

EvolutionWidget.defaultProps = {
  // PROPS
  numberDecimal: 0,
  percentOrDelta: "delta",
  evolutionThemeStyle: "defaultStyle",

  // STYLE
  comparisonColor: "#000000",
  comparisonSize: "1rem",
  comparisonWeight: "normal",
  iconPositiveSelected: "fas fa-arrow-up",
  iconPositiveColor: "#000000",
  iconPositiveFontSize: "1rem",
  iconNegativeSelected: "fas fa-arrow-down",
  iconNegativeColor: "#000000",
  iconNegativeFontSize: "1rem",
  iconNeutralFontSize: "1rem",
  iconNeutralColor: "#000000",
  iconNeutralSelected: "fas fa-arrow-right",
  customLabelColor: "#000000",
  customLabelSize: "1rem",
  customLabelWeight: "normal",
  valuePositiveColor: "#000000",
  valuePositiveSize: "1rem",
  valuePositiveWeight: "normal",
  valueNeutralColor: "#000000",
  valueNeutralSize: "1rem",
  valueNeutralWeight: "normal",
  valueNegativeColor: "#000000",
  valueNegativeSize: "1rem",
  valueNegativeWeight: "normal",
}

EvolutionWidget.propTypes = {
  // PROPS
  numberDecimal: PropTypes.number,
  percentOrDelta: PropTypes.oneOf(["percent", "delta"]),
  evolutionThemeStyle: PropTypes.oneOf(["defaultStyle", "customStyle"]),

  // STYLE
  comparisonColor: PropTypes.string,
  comparisonSize: PropTypes.string,
  comparisonWeight: PropTypes.string,
  iconPositiveSelected: PropTypes.string,
  iconPositiveColor: PropTypes.string,
  iconPositiveFontSize: PropTypes.string,
  iconNegativeSelected: PropTypes.string,
  iconNegativeColor: PropTypes.string,
  iconNegativeFontSize: PropTypes.string,
  iconNeutralFontSize: PropTypes.string,
  iconNeutralColor: PropTypes.string,
  iconNeutralSelected: PropTypes.string,
  customLabelColor: PropTypes.string,
  customLabelSize: PropTypes.string,
  customLabelWeight: PropTypes.string,
  valuePositiveColor: PropTypes.string,
  valuePositiveSize: PropTypes.string,
  valuePositiveWeight: PropTypes.string,
  valueNeutralColor: PropTypes.string,
  valueNeutralSize: PropTypes.string,
  valueNeutralWeight: PropTypes.string,
  valueNegativeColor: PropTypes.string,
  valueNegativeSize: PropTypes.string,
  valueNegativeWeight: PropTypes.string,
}

export default EvolutionWidget